/**
 * A filter bar's chip, in the pieces the filter bar needs.
 *
 * There is ONE filter bar in this product — `report-kit`'s `FilterBar` — and it
 * is mounted twice: over a report, writing into its spec, and over a
 * dashboard, writing into the URL. What differs between the two is what the
 * host hands it (the fields, the filters, where changes go, which chips are
 * pinned), not the bar. The chip lives here, apart from the bar, because it is
 * presentation and the bar is not: the bar knows what a dimension is.
 *
 * PRESENTATION ONLY, like everything else in this package: no dimension, no
 * model, no fetching. A chip is a name, a slot for whatever control supplies
 * the values, and — where the host allows it — a way off the bar.
 *
 * Four pieces rather than one component with a mode flag, because a chip's
 * values come either from its own panel or from a control the host brings,
 * and the two arrange the pieces differently:
 *
 *  - {@link FilterChip}        the pill itself, its pin and its ✕
 *  - {@link FilterChipLabel}   the dimension's name, for a chip whose values
 *                              come from a control the host brings
 *  - {@link FilterChipTrigger} name AND value as one button, for a chip that
 *                              opens a panel of its own
 *  - {@link FilterChipControl} the box a hosted control sits in, sized so the
 *                              bar does not move when a value is picked
 */

import { Pin, X } from 'lucide-react';
import type { ComponentProps, ReactNode, Ref } from 'react';
import { cn } from '../lib/utils';

export interface FilterChipProps {
  /**
   * The chip holds values. Drawn as an accented border — the only difference
   * between a filter that is narrowing the report and one that is not, since
   * an unset chip shows no value text at all.
   */
  active?: boolean;
  /**
   * Which filter this is — the dimension's id, or `period`.
   *
   * Emitted as `data-filter`. A bar's chips are otherwise indistinguishable
   * from the outside, so this is what lets a test or an e2e selector name one
   * without matching on the label a translation could move.
   */
  filterId?: string;
  /**
   * Take this filter off the bar. Omitted where the reader may not: a pinned
   * chip is the author's, and the period is on every bar.
   */
  onRemove?: () => void;
  /** Accessible name for the ✕. Say which filter — a bar has several. */
  removeLabel?: string;
  /**
   * The chip is PINNED: it stays on the bar for every reader, empty or not.
   *
   * Emitted as `data-pinned` and shown only on the author's toggle. A reader
   * sees no mark — the missing ✕ is enough, and a pin on every chip of a
   * dashboard bar was noise. Pinning is the host's decision; the chip only
   * carries it.
   */
  pinned?: boolean;
  /**
   * Pin or unpin this chip. Present only where the viewer may — a dashboard's
   * author while editing it. Without it a pinned chip shows no pin at all.
   */
  onPinToggle?: () => void;
  /** Accessible name for the pin button. Say which filter. */
  pinLabel?: string;
  className?: string;
  children?: ReactNode;
  /**
   * The pill element.
   *
   * Exposed because a hosted control has to anchor its panel to the WHOLE
   * chip. Anchored to the control alone, the panel opened indented by the
   * width of the dimension's name and read as belonging to nothing on the bar.
   */
  ref?: Ref<HTMLDivElement>;
}

export function FilterChip({
  active = false,
  filterId,
  onRemove,
  removeLabel,
  pinned = false,
  onPinToggle,
  pinLabel,
  className,
  children,
  ref,
}: FilterChipProps) {
  return (
    <div
      ref={ref}
      data-slot="filter-chip"
      data-filter={filterId}
      data-active={active || undefined}
      data-pinned={pinned || undefined}
      className={cn(
        // `relative`, because a chip may position its own panel against itself.
        'relative inline-flex items-center gap-1 rounded-full border bg-background p-0.5 text-xs',
        // A NOTCH darker than the bar's other borders, not `border-primary`.
        // Every chip on a report bar is active — the period always, and an
        // empty chip leaves the bar when its panel closes — so a primary
        // outline drew the whole row in heavy navy, the one dark border on a
        // page of light ones.
        active && 'border-ring/60',
        // Nothing trails a reader's pinned chip, and the hosted control strips
        // its own right padding for the ✕ or pin that normally follows the
        // caret — without this the caret sits against the border.
        pinned && !onPinToggle && !onRemove && 'has-[>[data-slot=filter-chip-control]]:pr-1.5',
        className,
      )}
    >
      {children}
      {onPinToggle ? (
        <button
          type="button"
          aria-label={pinLabel}
          aria-pressed={pinned}
          title={pinned ? 'Unpin from the bar' : 'Pin to the bar for everyone'}
          onClick={onPinToggle}
          className={cn(
            'inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-muted hover:text-foreground',
            pinned ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {/* Filled when pinned, outlined when not — one icon in two states
              reads as a toggle, where a pin and a crossed-out pin read as two
              different actions. */}
          <Pin className={cn('size-3', pinned && 'fill-current')} aria-hidden="true" />
        </button>
      ) : null}
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel}
          title="Remove"
          onClick={onRemove}
          className="inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="size-3" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

interface LabelProps {
  name: string;
  /**
   * One sentence telling this chip apart from another reading the same.
   *
   * `srController` and `accountSrController` are both "Sr. Controller" and mean
   * different people. A title rather than visible text because it is a
   * sentence, and only the ambiguous chips carry one — the ⓘ is what says a
   * chip HAS one, since a title nobody knows to hover for is not shown.
   */
  hint?: string;
  className?: string;
}

/** The dimension's name, beside a control the host brought. */
export function FilterChipLabel({ name, hint, className }: LabelProps) {
  return (
    <span
      data-slot="filter-chip-name"
      title={hint}
      // NO colour of its own. `FilterChipTrigger` draws this same name with
      // the chip's inherited colour, and the two kinds sit side by side on one
      // bar — a hosted Customer chip reading grey beside a built-in Channel
      // chip reading dark is the divergence this component exists to end.
      className={cn('shrink-0 pl-1.5 font-semibold', className)}
    >
      {name}
      {hint ? <span aria-hidden="true"> ⓘ</span> : null}
    </span>
  );
}

interface TriggerProps
  extends LabelProps,
    // `name` and `value` are a button's own form attributes; here they mean the
    // dimension and what it is set to, which is the reading that matters on a
    // filter bar. A chip is never a form control, so nothing is lost.
    Omit<ComponentProps<'button'>, 'name' | 'value' | 'className' | 'children'> {
  /**
   * What the filter is set to — omitted entirely when it is set to nothing.
   *
   * NOT the word "any". An empty chip is a transient state on both bars, so
   * the word labelled a state the reader is never left looking at, and on a
   * bar of four filters it read as four values that had been picked.
   *
   * A NODE rather than a string, because one value can have two parts that
   * should not read alike — the period chip's macro and the window it
   * currently resolves to. Anything falsy is still "set to nothing", so the
   * common case stays a plain string.
   */
  value?: ReactNode;
  expanded?: boolean;
}

/**
 * Name and value as ONE button, for a chip that opens a panel of its own.
 *
 * Passes everything it does not use straight through, so it can be the child
 * of a Radix `asChild` trigger — which is how the period chip gets a portaled
 * panel without this file knowing anything about popovers.
 */
export function FilterChipTrigger({
  name,
  hint,
  value,
  expanded,
  title,
  className,
  ...rest
}: TriggerProps) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      title={title ?? hint}
      className={cn(
        // `cursor-pointer` explicitly: a button's UA default is `cursor: default`
        // and nothing in Tailwind's preflight or the host app restores it, so
        // without this the chips are the only controls on the bar that do not
        // read as clickable.
        'inline-flex cursor-pointer items-baseline gap-1.5 rounded-full px-2 py-0.5 text-xs',
        className,
      )}
      {...rest}
    >
      <span data-slot="filter-chip-name" className="font-semibold">
        {name}
        {hint ? <span aria-hidden="true"> ⓘ</span> : null}
      </span>
      {value ? (
        <span data-slot="filter-chip-value" className="text-muted-foreground">
          {value}
        </span>
      ) : null}
    </button>
  );
}

/**
 * The box a hosted control sits in.
 *
 * SIZED TO ITS CONTENT, up to a cap. It was a fixed 150px so the chips to its
 * right would not move when a value was picked, but that left a one-value
 * chip like "FTE" with a gutter of dead space before its caret, and every chip
 * on the bar the same width whatever it held. The panel is anchored to the
 * chip's left edge, which does not move, so the one that is open stays put;
 * past the cap the selection scrolls inside (`badgeOverflow="scroll"`).
 *
 * The descendant rules strip a full-height form control down to chip
 * proportions. Without them a bar of four filters wrapped onto two lines, and
 * the pill is what makes the bar read as a row of chips whether a filter's
 * values come from a static list or from the app's own entity picker.
 */
export function FilterChipControl({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      data-slot="filter-chip-control"
      className={cn(
        'w-fit max-w-[260px] shrink-0',
        '[&_[role=combobox]]:h-auto [&_[role=combobox]]:min-h-5 [&_[role=combobox]]:rounded-full',
        '[&_[role=combobox]]:border-0 [&_[role=combobox]]:bg-transparent [&_[role=combobox]]:shadow-none',
        // `pr-0` and `gap-1`: the chip's own ✕ follows the caret, and the
        // picker's form-field spacing (gap-2 plus side padding) stacked with
        // the chip's gap left the caret and the ✕ reading as unrelated.
        '[&_[role=combobox]]:py-0 [&_[role=combobox]]:pl-1 [&_[role=combobox]]:pr-0 [&_[role=combobox]]:gap-1 [&_[role=combobox]]:text-xs',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A muted aside on the chip — a coverage summary, a count. */
export function FilterChipNote({ children }: { children: ReactNode }) {
  return (
    <span data-slot="filter-chip-note" className="pr-1 text-[10px] text-muted-foreground">
      {children}
    </span>
  );
}

/**
 * The bar the chips sit on.
 *
 * Only a layout — wrapping, because a bar of eight filters has to go somewhere,
 * and a leading label where the surface has room for one. The bar's own frame
 * (a border, a background) is the host's, passed in as `className`.
 */
export function FilterBarShell({
  label,
  busy = false,
  className,
  children,
}: {
  label?: string;
  /**
   * The bar's chips are still being worked out, so what is on it is not yet
   * the answer. Announced rather than merely drawn: a host that swaps its
   * chips for placeholders has changed what the bar MEANS, and a reader who
   * cannot see the placeholders would otherwise be told nothing at all.
   */
  busy?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      data-slot="filter-bar"
      aria-busy={busy}
      className={cn('flex flex-wrap items-center gap-1.5', className)}
    >
      {label && (
        <span className="font-semibold text-[10.5px] text-muted-foreground uppercase tracking-[0.07em]">
          {label}
        </span>
      )}
      {children}
    </div>
  );
}
